import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import {
  BACSCoefficient,
  EnergyCoefficient,
} from "src/model/enum/enumDigitalPlace";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";

@Component({
    selector: "app-popup-bacs-coefficient",
    templateUrl: "./popup-bacs-coefficient.component.html",
    styleUrls: ["./popup-bacs-coefficient.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupBACSCoefficientComponent
  implements OnInit, OnDestroy, iEvent, iTranslation {
  private _logContext: "Popup-bacs-coefficient.component";
  private _lblBacsAndTBMHighPerformance: string = "";
  private _lblBacsAndTBMAdvancede: string = "";
  private _lblBacsStandard: string = "";
  private _lblBacsNotEfficient: string = "";
  private _translateSubscription: Subscription = null;

  public _eventSubscriptions: Array<Subscription> = null;
  public BACSCoefficientOptions = null;
  public isHighlightedValue: number = 0;

  @Input() modalRef: ModalController;
  @Input() BACSSelected: number;

  constructor(
    private _modalController: ModalController,
    private _loggerService: LoggerService,
    private _eventService: EventService,
    private _translateService: TranslateService
  ) { }

  ngOnInit() {
    this.subscribeEvents();
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.loadBACSOption();
  }

  ngOnDestroy(): void {
    this.unsubscribeEvents();
  }

  /**
   * Close modal and return bacs selected
   * @param valSelected
   */
  public closeModal(bacsSelected?: number): void {
    this.modalRef.dismiss({
      data: bacsSelected,
    });
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.bacsAndTBMHighPerformance",
        "dictionary.bacsAndTBMAdvanced",
        "dictionary.bacsStandard",
        "dictionary.bacsNotEfficient",
      ])
      .subscribe((translation) => {
        this._lblBacsAndTBMHighPerformance =
          translation["dictionary.bacsAndTBMHighPerformance"];
        this._lblBacsAndTBMAdvancede =
          translation["dictionary.bacsAndTBMAdvanced"];
        this._lblBacsStandard = translation["dictionary.bacsStandard"];
        this._lblBacsNotEfficient = translation["dictionary.bacsNotEfficient"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Get the class of the chip of bacs
   * @param value
   * @returns
   */
  public getClassChip(value): string {
    let classChip: string = "leftChip ";
    classChip += " ion-chip-" + BACSCoefficient[value].toLowerCase();
    if (value == this.BACSSelected) {
      classChip += "-selected noPointer ";
    } else {
      if (value == this.isHighlightedValue) {
        classChip += "-selected";
      }
    }
    return classChip;
  }

  /**
   * On mouse over on div, set isHighlighted to changes css class of badge
   * @param value
   */
  public onMouseOver(value): void {
    this.isHighlightedValue = value;
  }

  /**
   * On mouse out on div, set isHighlighted to changes css class of badge
   */
  public onMouseOut(): void {
    this.isHighlightedValue = 0;
  }

  /**
   * Get whole label of bacs
   * @param value
   * @returns
   */
  public getLabelBACS(value): string {
    let valBACS: string = "";
    if (value == BACSCoefficient.A) {
      valBACS = this._lblBacsAndTBMHighPerformance;
    }
    if (value == BACSCoefficient.B) {
      valBACS = this._lblBacsAndTBMAdvancede;
    }
    if (value == BACSCoefficient.C) {
      valBACS = this._lblBacsStandard;
    }
    if (value == BACSCoefficient.D) {
      valBACS = this._lblBacsNotEfficient;
    }
    return valBACS;
  }

  /**
   * Get class of div that contains the label
   * @param value
   * @returns
   */
  public getBorderLabelBACS(value): string {
    let classChip: string = "border-explanation ";

    classChip += " ion-chip-border-" + BACSCoefficient[value].toLowerCase();
    if (value == this.BACSSelected) {
      classChip += "-selected noPointer ";
    } else {
      if (this.isHighlightedValue == value) {
        classChip += "-selected";
      }
    }
    return classChip;
  }

  /**
   * Get width of badge
   * @param value
   * @returns
   */
  public getWidthChip(value): string {
    let calculatedWidth = 15 + 100 - value;
    return calculatedWidth + "px";
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalController.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Convert ENUM BACS Coefficient into an array
   */
  private loadBACSOption() {
    this.BACSCoefficientOptions = Object.keys(BACSCoefficient)
      .filter((key) => /[A-G]\+*\**/.test(key))
      .map((key) => {
        return {
          label: key,
          value: parseInt(BACSCoefficient[key], 10),
        };
      });
  }
}
